import { formatTo12HourTime } from './lib/format-to-12-hour-time';
import { formatDateToReadable } from './lib/formatDateToReadable';
import { formatToLocale } from './lib/formatToLocale';
import { getFormattedBucketPrefix } from './lib/getFormattedBucketPrefix';
import { getFormattedDateInDDMMMYYYY } from './lib/getFormattedDateInDDMMMYYYY';
import { getFormattedDateInISO } from './lib/getFormattedDateInISO';
import { getFormattedDateInMMMDD } from './lib/getFormattedDateInMMMDD';
import { getFormattedDateInMMMMdyyyyWithTime } from './lib/getFormattedDateInMMMMdyyyyWithTime';
import { getFormattedDateWithTimePeriodSuffix } from './lib/getFormattedDateWithTimePeriodSuffix';
import { getFormattedLocalDateTime } from './lib/getFormattedLocalDateTime';
import { getUtcOffsetFromTimeZone } from './lib/getUtcOffsetFromTimeZone';
import { timestampToHumanReadable } from './lib/timestamp-to-human-readable';
import { toDateTimeLocalValue } from './lib/toDateTimeLocalValue';

export * from './lib/age';
export * from './lib/formatToLocale';
export * from './lib/url-date';
export * from './lib/timestamp-to-human-readable';
export * from './lib/convert-time-to-utc';
export * from './lib/format-to-12-hour-time';
export * from './lib/format-seconds-to-time';
export * from './lib/format-time-remaining';
export * from './lib/add-one-hour';
export * from './lib/format-time-display';
export * from './lib/getFormattedDateWithTimePeriodSuffix';
export * from './lib/getFormattedDateInDDMMMYYYY';
export * from './lib/getFormattedLocalDateTime';
export * from './lib/getFormattedDateInMMMMdyyyyWithTime';
export * from './lib/formatDateToReadable';
export * from './lib/getFormattedUtteranceTime';
export * from './lib/getFormattedDateInISO';
export * from './lib/isValidTimestamp';
export * from './lib/toDateTimeLocalValue';
export * from './lib/toWallClockString';
export * from './lib/toWallClockStringUTC';
export * from './lib/localTimeToUTC';

/**
 * Collection of date formatting utilities
 */
export const formatDate = {
  /**
   * Returns the UTC offset (e.g. "-07:00") for the given IANA time zone.
   *
   * @example
   * getUtcOffsetFromTimeZone('America/Los_Angeles'); // "-07:00"
   */
  getUtcOffsetFromTimeZone,

  /**
   * Formats a UTC date to a readable string in a fixed UTC offset (e.g., "-07:00").
   *
   * @param date - A Date object or ISO string in UTC.
   * @param offset - UTC offset string (e.g., "-07:00"). Defaults to "-07:00".
   * @example "March 20, 2025"
   */
  toDisplay: formatDateToReadable,

  /**
   * Formats a date to ISO format
   * @example "1983-04-13"
   */
  toISO: getFormattedDateInISO,

  /**
   * Formats a date to short format with 3-letter month
   * @example "19 Mar 2025"
   */
  toShort: getFormattedDateInDDMMMYYYY,

  /**
   * Formats a date to "MMM dd" format
   * @example "Mar 19"
   */
  toMMMDD: getFormattedDateInMMMDD,

  /**
   * Converts a date to local date-time format
   * @example "18 Mar 2025, 2:45 PM"
   */
  toLocalDateTime: getFormattedLocalDateTime,

  /**
   * Formats time to 12-hour format
   * @example "9:05 AM"
   */
  to12HourTime: formatTo12HourTime,

  /**
   * Formats date according to locale settings
   * @example "3/20/2025 14:30"
   */
  toLocale: formatToLocale,

  /**
   * Converts a timestamp to human-readable format
   * @example "just now" | "5 minutes ago" | "2 hours ago" | "3 days ago"
   */
  toHumanReadable: timestampToHumanReadable,

  /**
   * Formats a given date (string or Date object) into "MMMM d, yyyy h:mm a" format.
   * Optionally, it can adjust the date based on a provided UTC offset.
   *
   * @param date - The input date (UTC).
   * @param timezoneLocale - A string like 'America/Los_Angeles'.
   * @example output: March 20, 2025 10:43 PM
   */
  toMMMMdyyyyWithTime: getFormattedDateInMMMMdyyyyWithTime,

  /**
   * Formats date with time period suffix
   * @example "3/19/2025 10:30 AM"
   */
  withTimePeriodSuffix: getFormattedDateWithTimePeriodSuffix,

  /**
   * Formats a a given date to yyyy-MM-dd-HH-mm-ss format.
   * @example "2025-03-19-10-30-00"
   */
  toBucketPrefix: getFormattedBucketPrefix,

  /**
   * Converts a date string to the format required by HTML datetime-local input.
   * @example "2025-12-30T00:00"
   */
  toDateTimeLocalValue,
};
